'use client';
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  fetchClaims,
  createClaim,
  updateClaim,
  setActiveClaim,
  type Claim,
} from '@/lib/claimService';
import { createClient } from '@/lib/supabase/client';

interface ClaimContextValue {
  claims: Claim[];
  activeClaim: Claim | null;
  unpricedCount: number;
  loadingClaims: boolean;
  refreshClaims: () => Promise<void>;
  switchClaim: (id: string) => Promise<void>;
  addClaim: (name: string) => Promise<Claim | null>;
  renameClaim: (id: string, name: string) => Promise<void>;
}

const ClaimContext = createContext<ClaimContextValue>({
  claims: [],
  activeClaim: null,
  unpricedCount: 0,
  loadingClaims: true,
  refreshClaims: async () => {},
  switchClaim: async () => {},
  addClaim: async () => null,
  renameClaim: async () => {},
});

export function ClaimProvider({ children }: { children: React.ReactNode }) {
  const [claims, setClaims] = useState<Claim[]>([]);
  const [unpricedCount, setUnpricedCount] = useState(0);
  const [loadingClaims, setLoadingClaims] = useState(true);

  // Keep a ref to the active claim id so subscriptions can access it without stale closures
  const activeClaimIdRef = useRef<string | null>(null);

  const activeClaim = claims.find((c) => c.isActive) ?? null;

  const refreshClaims = useCallback(async () => {
    setLoadingClaims(true);
    const data = await fetchClaims();
    setClaims(data);
    setLoadingClaims(false);
  }, []);

  // Fetch unpriced count for active claim
  const refreshUnpriced = useCallback(async (claimId: string | null) => {
    if (!claimId) { setUnpricedCount(0); return; }
    const supabase = createClient();
    const { count } = await supabase
      .from('ct_claim_items')
      .select('id', { count: 'exact', head: true })
      .eq('claim_id', claimId)
      .eq('price_status', 'unpriced');
    setUnpricedCount(count ?? 0);
  }, []);

  useEffect(() => {
    refreshClaims();
  }, [refreshClaims]);

  useEffect(() => {
    activeClaimIdRef.current = activeClaim?.id ?? null;
    refreshUnpriced(activeClaim?.id ?? null);
  }, [activeClaim?.id, refreshUnpriced]);

  // ── Realtime subscriptions ────────────────────────────────────────────────
  useEffect(() => {
    const supabase = createClient();

    // Subscribe to ct_claims table — any INSERT/UPDATE/DELETE refreshes claim list
    const claimsChannel = supabase
      .channel('rt-ct-claims')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'ct_claims' },
        () => {
          refreshClaims();
        }
      )
      .subscribe();

    // Subscribe to ct_claim_items — only refresh unpriced count (no inventoryVersion bump)
    const itemsChannel = supabase
      .channel('rt-ct-claim-items-ctx')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'ct_claim_items' },
        () => {
          refreshUnpriced(activeClaimIdRef.current);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(claimsChannel);
      supabase.removeChannel(itemsChannel);
    };
  }, [refreshClaims, refreshUnpriced]);
  // ─────────────────────────────────────────────────────────────────────────

  const switchClaim = useCallback(async (id: string) => {
    await setActiveClaim(id);
    await refreshClaims();
  }, [refreshClaims]);

  const addClaim = useCallback(async (name: string): Promise<Claim | null> => {
    const claim = await createClaim(name);
    if (claim) await refreshClaims();
    return claim;
  }, [refreshClaims]);

  const renameClaim = useCallback(async (id: string, name: string) => {
    await updateClaim(id, { name });
    await refreshClaims();
  }, [refreshClaims]);

  return (
    <ClaimContext.Provider value={{
      claims,
      activeClaim,
      unpricedCount,
      loadingClaims,
      refreshClaims,
      switchClaim,
      addClaim,
      renameClaim,
    }}>
      {children}
    </ClaimContext.Provider>
  );
}

export function useClaimContext() {
  return useContext(ClaimContext);
}
